import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Folder, FileText, Clock, HardDrive, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { pwaCacheManager, CachedPage } from "@/utils/pwaCacheManager";
import { cn } from "@/lib/utils";

interface CachedContentBrowserProps {
  onPageSelect?: (url: string) => void;
}

export function CachedContentBrowser({
  onPageSelect,
}: CachedContentBrowserProps) {
  const [cachedPages, setCachedPages] = useState<CachedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [cacheStats, setCacheStats] = useState<any>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadCachedContent();
  }, []);

  const loadCachedContent = async () => {
    setLoading(true);
    try {
      const [pages, stats] = await Promise.all([
        pwaCacheManager.getAllCachedPages(),
        pwaCacheManager.getCacheStats(),
      ]);
      setCachedPages(pages);
      setCacheStats(stats);
    } catch (error) {
      console.error("Failed to load cached content:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = (page: CachedPage) => {
    if (onPageSelect) {
      onPageSelect(page.url);
    } else {
      navigate(page.url);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? "s" : ""} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    return "Less than an hour ago";
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-destructive/20 text-destructive border-destructive/30";
      case "medium":
        return "bg-warning/20 text-warning border-warning/30";
      case "low":
        return "bg-success/20 text-success border-success/30";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
            <span className="ml-2 text-muted-foreground">
              Loading cached content...
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cache Statistics */}
      {cacheStats && (
        <Card>
          <CardHeader className="pb-4">
            <div className="flex items-center">
              <HardDrive className="w-5 h-5 mr-2 text-primary-light" />
              <CardTitle className="text-lg">Cache Statistics</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Total Size</p>
              <p className="font-medium">{formatBytes(cacheStats.totalSize)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Cached Pages</p>
              <p className="font-medium">{cacheStats.pageCount}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Updated</p>
              <p className="font-medium">
                {cacheStats.lastUpdate
                  ? formatTimeAgo(cacheStats.lastUpdate)
                  : "Never"}
              </p>
            </div>
            <div>
              <p className="text-muted-foreground">Storage Used</p>
              <p className="font-medium">
                {cacheStats.quota
                  ? `${((cacheStats.usage / cacheStats.quota) * 100).toFixed(1)}%`
                  : "Unknown"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Cached Pages List */}
      <Card>
        <CardHeader>
          <div className="flex items-center">
            <Folder className="w-5 h-5 mr-2 text-primary-light" />
            <CardTitle className="text-lg">Available Offline Content</CardTitle>
          </div>
          <CardDescription>
            {cachedPages.length > 0
              ? `${cachedPages.length} page${cachedPages.length > 1 ? "s" : ""} cached for offline viewing`
              : "No content cached yet"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {cachedPages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No cached content available</p>
              <p className="text-sm">
                Content will be cached as you browse while online
              </p>
            </div>
          ) : (
            cachedPages.map((page) => (
              <div
                key={page.url}
                className={cn(
                  "flex items-center justify-between p-3 rounded-lg border border-border bg-card/30 hover:bg-card/50 transition-colors cursor-pointer",
                )}
                onClick={() => handlePageClick(page)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <h4 className="font-medium truncate">
                      {page.title || page.url}
                    </h4>
                    <Badge
                      variant="outline"
                      className={cn("text-xs", getPriorityColor(page.priority))}
                    >
                      {page.priority}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTimeAgo(page.timestamp)}
                    </span>
                    <span>{formatBytes(page.size)}</span>
                    <span className="truncate">{page.url}</span>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-2 flex-shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePageClick(page);
                  }}
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {cachedPages.length > 0 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={loadCachedContent}
            className="w-full"
          >
            Refresh Cached Content
          </Button>
        </div>
      )}
    </div>
  );
}
